import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { reduxForm, propTypes as formPropTypes } from 'redux-form';
import StickyBox from 'react-sticky-box';
import { userAccountTypes, userDepartmentTypes, userTypes, departmentsTypes } from '../../types/user';

import {
  createNewReturn,
  requestUnsubmittedReturnDetails,
  updateReturnDetails,
  setIsReturnStep,
  setReturnData,
  setOriginatesFromReturnStatus,
  submitReturn,
  setSelectedReturnId,
  clearPreviousReturnStepData,
  requestReturnsById,
  requestSubmittedReturns
} from './actions';

import { requestOrderDetails } from '../actions';

import {
  getReturnCreateResponse,
  originatesFromReturnStatus,
  getReturnData,
  getOrderReturnDetails,
  selectedReturnId,
  isSubmitting,
  detailUpdate,
  getSubmittedReturnDetails,
  submittedReturns
} from './reducer';
import { isFetching, previousLocationWasReturnStatus, orderDetailsData } from '../reducer';
import { billableDepartments, getUserAccount, selectDepartments, getUserData } from '../../user/selectors';
import { getSalesOrg } from '../../gamedayAccount/selectors';
import { searchTerm } from '../../global/reducer';
import { showToast } from '../../components/toast/actions';
import { openModal, closeModal, disableModal } from '../../components/modal/actions';

import {
  SECTION_DETAIL,
  SECTION_SELECT,
  SECTION_FINALIZE,
  SECTION_SUBMITTED,
  PER_PAGE,
  TODAY,
  RETURN_REASONS,
  PAYMENT_METHOD_FREE
} from './constants';
import { DEFAULT_ONPOPSTATE } from '../../app/constants';

import { mapDepartmentName } from '../status/util';
import { toTitleCase } from '../../util/toTitleCase';
import { filterListNested } from '../../util/filterListNested';
import getReturnableProductQuantities from '../../util/getReturnableProductQuantities';

import { TranslatedCopy } from '../../intl/TranslatedCopy';

import { H1 } from '~COMPONENTS/containers/Headers';
import LoadingMessage from '~COMPONENTS/spinner/LoadingMessage';
import NoResults from '../../components/NoResults';

import OrderReturnsStatusSummary from './status/OrderReturnsStatusSummary';
import OrderReturnsStatusDetails from './status/OrderReturnsStatusDetails';
import OrderReturnsStatusFinal from './status/OrderReturnsStatusFinal';
import OrderReturnsPageSearch from './page/OrderReturnsPageSearch';
import OrderReturnsPageDetails from './page/details/OrderReturnsPageDetails';
import OrderReturnsPageSelect from './page/select/OrderReturnsPageSelect';
import OrderReturnsPageSelectHeader from './page/select/OrderReturnsPageSelectHeader';
import OrderReturnsPageFinalContact from './page/final/OrderReturnsPageFinalContact';
import OrderReturnsPageFinalDetails from './page/final/OrderReturnsPageFinalDetails';
import OrderReturnsPageSubmitted from './page/submitted/OrderReturnsPageSubmitted';
import OrderReturnsNavigation from './OrderReturnsNavigation';
import OrderReturnsSubmittedBar from './OrderReturnsSubmittedBar';
import { StepLabelStyled, StepperStyled, StepStyled } from '../../components/stepper';

export class OrderReturns extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentStep: 0,
      steps: [
        {
          label: new TranslatedCopy({ id: 'orderreturns.returnDetails' }).toString(),
          section: SECTION_DETAIL
        },
        {
          label: new TranslatedCopy({ id: 'orderreturns.selectProducts' }).toString(),
          section: SECTION_SELECT
        },
        {
          label: new TranslatedCopy({ id: 'orderreturns.finalizeReturn' }).toString(),
          section: SECTION_FINALIZE
        }
      ],
      filteredList: null,
      submittableReturnsList: null,
      searchTerm: '',
      hasMore: true,
      page: 1,
      currentAddress: null,
      errorMessageId: null,
      isSaving: false,
      isSelectAllProducts: false,
      hasQuantityError: false,
      isLoading: false
    };
  }

  async componentDidMount() {
    const {
      history,
      setIsReturnStep,
      previousLocationWasReturnStatus,
      setOriginatesFromReturnStatus,
      requestOrderDetails,
      requestSubmittedReturns,
      selectedReturnId
    } = this.props;
    setIsReturnStep(true);

    const orderId = this.getOrderId();

    this.setState({ isLoading: true });

    await requestSubmittedReturns(orderId);
    await requestOrderDetails(orderId);

    if (selectedReturnId) {
      await this.fetchUnsubmittedReturnDetails();
    } else {
      this.setState({ isLoading: false });
    }

    this.setReturnReference();
    this.initiateStep(0);

    if (previousLocationWasReturnStatus) setOriginatesFromReturnStatus(true);

    history.push(`/orders/returns/id/${orderId}/1`);
    window.onpopstate = () => {
      const { currentStep } = this.state;

      if (currentStep === 0) {
        this.onBackClickHandler();
        return;
      }
      if (currentStep > 2) {
        history.push(this.getHeaderPath());
        return;
      }
      this.sectionProgress(currentStep - 1);
      this.scrollToTop();
    };
  }

  componentWillUnmount() {
    const { setIsReturnStep, setSelectedReturnId, clearPreviousReturnStepData } = this.props;
    setIsReturnStep(false);
    setSelectedReturnId('');
    clearPreviousReturnStepData();
    window.onpopstate = DEFAULT_ONPOPSTATE;
  }

  setReturnReference = () => {
    const { change, previousLocationWasReturnStatus, orderDetails } = this.props;
    const { steps, currentStep } = this.state;
    // Fail-safe
    if (previousLocationWasReturnStatus) return;

    this.resetPage();
    if (steps[currentStep] && steps[currentStep].section === SECTION_DETAIL) {
      change('returnReference', orderDetails?.sapOrderNumber || TODAY);
    }
  };

  onQuantityFormValueUpdate = (e, productCode, sizeCode) => {
    const val = this.formatFormValue(e);

    this.updateSubmittableReturnList([
      {
        productCode,
        quantity: val !== '' ? val : 0,
        sizeCode,
        sizeDisplay: e.displayValue // ??
      }
    ]);
  };

  setHasQuantityError = value => {
    this.setState({ hasQuantityError: value });
  };

  onFormValueUpdate = (e, type) => {
    if (!type || type === '') return;
    if (this.getOrderReturnId() === 0) return;

    this.updateRecord({
      [`${type}`]: type === 'reasonCode' ? parseInt(e.value, 10) : this.formatFormValue(e, type)
    });
  };

  onExitConfirm = () => {
    const { closeModal, history } = this.props;
    closeModal();
    history.push(this.getHeaderPath());
  };

  onBackClickHandler = (e = null) => {
    const { showToast, openModal, closeModal } = this.props;
    const { steps, currentStep } = this.state;
    const section = steps[currentStep] ? steps[currentStep].section : SECTION_SUBMITTED;

    if (section === SECTION_SELECT || section === SECTION_FINALIZE)
      showToast(
        {
          type: 'success',
          icon: 'filled-check',
          message: new TranslatedCopy({ id: 'orderreturns.unsubmitted.save' }).toString()
        },
        3500
      );
    if (section !== SECTION_DETAIL) return;
    if (e) e.preventDefault();
    openModal({
      type: 'alert',
      noPadding: true,
      extraClassName: 'prl8-sm pb4-sm',
      content: (
        <>
          <H1 className='u-full-width mb3-sm center text-color-primary-dark'>
            <TranslatedCopy id='orderreturns.exitWithoutSave' />
          </H1>
          <p className='ta-sm-l'>
            <TranslatedCopy id='orderreturns.prompt.copy' />
          </p>
        </>
      ),
      confirmText: <TranslatedCopy id='orderreturns.exitReturn' />,
      confirmAction: this.onExitConfirm,
      cancelText: <TranslatedCopy id='global.cancel' />,
      cancelAction: closeModal
    });
  };

  setCurrentAddress = currentAddress => {
    this.setState({ currentAddress });

    if (!currentAddress) return;

    if (currentAddress.type === 'SAP') {
      this.updateRecord({
        addressId: currentAddress.account,
        addressType: 'SAP'
      });
    } else {
      this.updateRecord({
        addressId: currentAddress.id,
        addressType: 'GAMEDAY'
      });
    }
  };

  getHeaderPath = () => {
    const { previousLocationWasReturnStatus, originatesFromReturnStatus } = this.props;
    return previousLocationWasReturnStatus || originatesFromReturnStatus ? '/orders/returns' : '/orders/status';
  };

  setInitialContactForm = () => {
    const { user, returnsForm, change } = this.props;
    if (!user) return;
    const { firstName, lastName, phoneNumber, extension, email } = user;
    const fullName = `${firstName} ${lastName}`;

    change(
      'contactName',
      returnsForm && returnsForm.values && returnsForm.values.contactName
        ? returnsForm.values.contactName
        : fullName || ''
    );
    change(
      'contactPhoneNumber',
      returnsForm && returnsForm.values && returnsForm.values.contactPhoneNumber
        ? returnsForm.values.contactPhoneNumber
        : phoneNumber || ''
    );
    change(
      'contactExtNumber',
      returnsForm && returnsForm.values && returnsForm.values.contactExtension
        ? returnsForm.values.contactExtension
        : extension || ''
    );
    change(
      'contactEmail',
      returnsForm && returnsForm.values && returnsForm.values.contactEmail
        ? returnsForm.values.contactEmail
        : email || ''
    );

    this.updateRecord({
      contactName: fullName || '',
      contactEmail: email || '',
      contactPhoneNumber: phoneNumber || ''
    });
  };

  getReturnableProducts = () => {
    const { orderDetails } = this.props;

    const returnableProductQuantities = getReturnableProductQuantities(orderDetails.productDetails);

    // Attempt returnable pull from return details
    if (returnableProductQuantities) {
      return returnableProductQuantities;
    }

    // Not found
    return null;
  };

  getReturnableProduct = productCode => {
    const returnableProducts = this.getReturnableProducts();
    return returnableProducts ? returnableProducts.filter(prod => prod.productCode === productCode)[0] || null : null;
  };

  getProductNetAmount = productCode => {
    const productDetails = this.getProductDetails();
    return productDetails && productDetails[productCode]
      ? productDetails[productCode].totalNet / this.getReturnableProduct(productCode).totalReturnableQuantity
      : 0;
  };

  getTotal = (netAmount = false) => {
    const { submittableReturnsList } = this.state;
    return submittableReturnsList
      ? submittableReturnsList.reduce(
          (sum, current) =>
            sum +
            ((parseInt(current.quantity, 10) || 0) *
              (netAmount && this.getReturnableProduct(current.productCode)
                ? this.getProductNetAmount(current.productCode)
                : 1) || 0),
          0
        )
      : 0;
  };

  getTotalItems = () => {
    const { submittableReturnsList } = this.state;
    return submittableReturnsList
      ? submittableReturnsList.reduce((sum, current) => sum + (parseInt(current.quantity, 10) > 0 ? 1 : 0), 0)
      : 0;
  };

  getHardFilteredList = (crop = false) => {
    const { filteredList, page } = this.state;
    const returnableProducts = this.getReturnableProducts();
    let hardFiltered = [];
    if (filteredList && filteredList.length > 0 && returnableProducts && returnableProducts.length > 0) {
      for (let i = 0, l = returnableProducts.length; i < l; i++) {
        hardFiltered = hardFiltered.concat(
          filteredList.filter(({ productCode }) => productCode === returnableProducts[i].productCode)
        );
      }
    }
    return crop ? this.cropList(hardFiltered, page) : hardFiltered;
  };

  getOrderId = () => {
    if (!window || !window.location || !window.location.pathname) return 0;

    const splitPath = window.location.pathname.split('/');
    return splitPath[splitPath.length - 1].toString().length > 1
      ? splitPath[splitPath.length - 1]
      : splitPath[splitPath.length - 2];
  };

  getOrderReturnId = () => {
    const { selectedReturnId, createReturnResponse } = this.props;
    if (selectedReturnId && selectedReturnId !== '') return selectedReturnId;
    if (createReturnResponse && createReturnResponse.id) return createReturnResponse.id;
    return 0;
  };

  getProductDetails = () => {
    const { orderDetails } = this.props;

    if (orderDetails && orderDetails.productDetails) return orderDetails.productDetails;
    return null;
  };

  formatFormValue = (e, type) => {
    if (type === 'reasonCode') return e.value;
    // --
    let i = 0;
    let str = '';
    while (e[i] !== undefined && e[i] !== 'undefined') str += e[i++];
    return str;
  };

  getSubmittableReturnListWholesaleTotal = () => {
    const { submittableReturnsList } = this.state;
    return submittableReturnsList
      ? submittableReturnsList.reduce((sum, curr) => sum + curr.wholesale * curr.quantity, 0)
      : 0;
  };

  updateSubmittableReturnList = (updates = []) => {
    const { submittableReturnsList } = this.state;
    let submittableReturnsListCopy = [];

    if (!submittableReturnsList) {
      const returnableProducts = this.getReturnableProducts();
      const returnableProductsBySize = [];
      returnableProducts.forEach(product => {
        product.sizes.forEach(size => {
          if (size.returnableQuantity > 0) {
            returnableProductsBySize.push({
              productCode: product.productCode,
              sizeCode: size.code,
              quantity: 0,
              wholesale: product.wholesaleAmount,
              sizeDisplay: product.sizeDisplay
            });
          }
        });
      });
      submittableReturnsListCopy = [...returnableProductsBySize];
    } else {
      submittableReturnsListCopy = [...submittableReturnsList];
    }

    // Update
    updates.forEach(update => {
      for (let i = 0, l = submittableReturnsListCopy.length; i < l; i++) {
        if (
          submittableReturnsListCopy[i].productCode === update.productCode &&
          submittableReturnsListCopy[i].sizeCode === update.sizeCode
        ) {
          submittableReturnsListCopy[i].quantity = parseInt(update.quantity, 10);
          break;
        }
      }
    });

    this.setState({ submittableReturnsList: submittableReturnsListCopy }, () => {
      const productToReturn = submittableReturnsListCopy
        .filter(product => product.quantity > 0)
        .map(product => ({
          productCode: product.productCode,
          sizeCode: product.sizeCode,
          quantity: parseInt(product.quantity, 10),
          sizeDisplay: product.sizeDisplay
        }));

      this.updateRecord({
        productToReturn
      });
    });

    this.filterReturnsList();
  };

  scrollToTop = () => {
    window.scroll(0, 0);
  };

  setIsSelectAllProducts = isSelectAllProducts => {
    this.setState({ isSelectAllProducts });
  };

  selectAllProducts = () => {
    const updateList = [];
    const returnableProducts = this.getReturnableProducts();

    returnableProducts.forEach(({ productCode, sizes }) => {
      sizes.forEach(({ code, returnableQuantity }) => {
        if (returnableQuantity > 0) {
          updateList.push({
            productCode,
            quantity: returnableQuantity,
            sizeCode: code
          });
        }
      });
    });

    this.updateSubmittableReturnList(updateList);
    this.setIsSelectAllProducts(true);
  };

  updateSearchTerm = async val => {
    await this.setState({ searchTerm: val });
    this.resetPage();
  };

  fetchUnsubmittedReturnDetails = () => {
    const { requestUnsubmittedReturnDetails, change, user } = this.props;
    const { firstName, lastName, phoneNumber, email } = user;

    const orderReturnId = this.getOrderReturnId();

    if (orderReturnId === 0) return;

    requestUnsubmittedReturnDetails({
      orderReturnId
    }).then(({ type, payload }) => {
      if (type && type.indexOf('SUCCESS') !== -1) {
        if (!payload) return;

        const {
          reasonCode,
          referenceId,
          comments,
          contactName,
          contactPhoneNumber,
          contactEmail,
          productToReturn
        } = payload;

        const {
          orderDetails: { sapOrderNumber }
        } = this.props;

        change('returnReason', reasonCode.toString());
        change('returnReference', referenceId);
        change('returnComments', comments);
        change('contactName', contactName || `${firstName} ${lastName}`);
        change('contactPhoneNumber', contactPhoneNumber || phoneNumber || '0000000000');
        change('contactEmail', contactEmail || email);
        change('returnReference', referenceId || sapOrderNumber || TODAY);

        this.updateSubmittableReturnList(productToReturn);
      } else {
        this.setState({ errorMessageId: 'returnStatus.noResults' });
      }

      this.setState({ isLoading: false });
    });
  };

  createNewRecord = () => {
    if (this.getOrderReturnId() !== 0) return;
    const {
      returnsForm: {
        values: { returnReason, returnComments, returnReference }
      },
      createNewReturn
    } = this.props;

    createNewReturn({
      orderId: this.getOrderId(),
      reasonCode: String(parseInt(returnReason, 10)),
      comments: returnComments,
      referenceId: returnReference
    });
  };

  updateRecord = data => {
    const { updateReturnDetails } = this.props;
    return updateReturnDetails({
      ...data,
      orderReturnId: this.getOrderReturnId()
    });
  };

  sectionProgress = (stepOverride = null) => {
    const { currentStep, steps } = this.state;
    if (stepOverride !== null && (stepOverride === currentStep || stepOverride >= currentStep)) return;
    if (stepOverride === null && steps[currentStep] && steps[currentStep].section === SECTION_FINALIZE) {
      this.submitReturn();
      return;
    }
    this.stepUpdate(stepOverride);
  };

  stepUpdate = (override = null) => {
    const { currentStep: stateCurrentStep } = this.state;
    const currentStep = override !== null ? override : stateCurrentStep + 1;

    this.setState({ currentStep });
    this.initiateStep(currentStep);
  };

  initiateStep = index => {
    const { history, setIsReturnStep } = this.props;
    const { steps } = this.state;
    switch (steps[index] ? steps[index].section : SECTION_SUBMITTED) {
      case SECTION_DETAIL:
        this.resetPage();
        break;

      case SECTION_SELECT:
        this.createNewRecord();
        break;

      case SECTION_FINALIZE:
        this.setInitialContactForm();
        break;

      case SECTION_SUBMITTED:
        setIsReturnStep(false);
        break;

      default:
        break;
    }

    history.push(`/orders/returns/id/${this.getOrderId()}/${index + 1}`);
    this.scrollToTop();
  };

  submitReturn = () => {
    const { returnsForm, account, setReturnData, submitReturn, showToast, requestReturnsById } = this.props;
    const { currentAddress, submittableReturnsList } = this.state;
    const { values } = returnsForm;

    this.setState({ isSaving: true }, () =>
      this.updateRecord({
        contactName: values.contactName,
        contactEmail: values.contactEmail,
        contactPhoneNumber: values.contactPhoneNumber
      })
        .then(() => {
          setReturnData({
            name: values.contactName,
            phone: values.contactPhoneNumber,
            extension: values.contactExtension || '',
            email: values.contactEmail,
            accountName: account.name,
            address: {
              attention: currentAddress.attention || currentAddress.name,
              addressLine1: currentAddress.addressLine1 || '',
              addressLine2: currentAddress.addressLine2 || '',
              streetAddressLine1: currentAddress.streetAddressLine1 || '',
              city: currentAddress.city || '',
              state: currentAddress.state || '',
              zipCode: currentAddress.zipCode || ''
            },
            returnReason: values.returnReason,
            returnComments: values.returnComments,
            returnReference: values.returnReference,
            products: submittableReturnsList
          });

          submitReturn(this.getOrderReturnId()).then(({ type, payload }) => {
            this.setState({ isSaving: false });
            if (type && type.indexOf('SUCCESS') === -1) {
              showToast(
                {
                  type: 'error',
                  message: new TranslatedCopy({
                    id: 'orderreturns.submitError',
                    values: {
                      errorNo: payload.error.toString().split('::')[0].toString().split(': ')[1].toString() || '000'
                    }
                  }).toString()
                },
                3500
              );
            } else {
              const returnIds = payload.map(r => r.body?.returnId);
              requestReturnsById(returnIds);
              this.stepUpdate();
            }
          });
        })
        .catch(() => this.setState({ isSaving: false }))
    );
  };

  resetPage = () => {
    const filtered = this.filterReturnsList();
    this.setState({ filteredList: filtered, page: 1, hasMore: this.blnHasMore(filtered) });
  };

  addPage = () => {
    const filteredList = this.filterReturnsList();
    const { isFetching } = this.props;
    const { page, hasMore } = this.state;
    if (hasMore && !isFetching) {
      this.setState({ filteredList, page: page + 1, hasMore: this.blnHasMore(filteredList, page + 1) });
    }
  };

  blnHasMore = (list, page = 1) => list.length > PER_PAGE * page;

  loadInfiniteScrollReturns = () => {
    const { isFetching } = this.props;
    const { hasMore } = this.state;
    if (hasMore && !isFetching) this.addPage();
  };

  filterReturnsList = () => {
    const { searchTerm } = this.state;
    const { change } = this.props;
    const productDetails = this.getProductDetails();
    if (!productDetails) return [];

    const returnableProducts = this.getReturnableProducts();
    if (returnableProducts && returnableProducts.length > 0) {
      for (let i = 0, l = returnableProducts.length; i < l; i++) {
        const { productCode, totalReturnableQuantity } = returnableProducts[i];
        change(`${productCode}_limit`, parseInt(totalReturnableQuantity, 10));
      }
    }

    return (
      filterListNested(
        productDetails
          ? Object.keys(productDetails)
              .map(key => productDetails[key])
              .filter(product => product.returnable)
          : [],
        ['copy.name', 'productCode'],
        searchTerm
      ) || []
    );
  };

  cropList = (list, page) => (list ? list.slice(0, this.blnHasMore(list, page) ? page * PER_PAGE : list.length) : []);

  downloadConfirm = () => {
    const { closeModal, disableModal } = this.props;
    // TODO: Generate return report here
    closeModal();
    disableModal();
  };

  download = e => {
    e.preventDefault();
    // TODO: Open return-download modal here
  };

  print = e => {
    e.preventDefault();
    window.print();
  };

  render() {
    const {
      billableDepartments,
      account: { name: accountName, number: accountNumber },
      returnsForm,
      valid,
      userAllDepartments,
      previousLocationWasReturnStatus,
      originatesFromReturnStatus,
      returnData,
      isSubmitting,
      detailUpdate,
      orderDetails,
      submittedReturnDetails,
      salesOrg,
      submittedReturns
    } = this.props;
    const {
      currentStep,
      steps,
      hasMore,
      searchTerm,
      errorMessageId,
      isSaving,
      isSelectAllProducts,
      submittableReturnsList,
      hasQuantityError,
      currentAddress,
      isLoading
    } = this.state;
    const section = steps[currentStep] ? steps[currentStep].section : SECTION_SUBMITTED;

    if (!orderDetails || isLoading) {
      return (
        <div className='u-full-width flex-container align-center bg-primary-grey'>
          {section !== SECTION_SUBMITTED && (
            <OrderReturnsNavigation
              backTo={this.getHeaderPath()}
              backToText={
                previousLocationWasReturnStatus || originatesFromReturnStatus
                  ? 'global.backToReturns'
                  : 'global.backToOrders'
              }
              onBackClickHandler={this.onBackClickHandler}
            />
          )}
          {errorMessageId ? (
            <NoResults titleId={errorMessageId} />
          ) : (
            <LoadingMessage title={new TranslatedCopy({ id: 'returnDetails.return' }).toString()} />
          )}
        </div>
      );
    }

    const {
      departmentNumber,
      paymentMethod,
      orderType,
      customerPO,
      submittedBy,
      orderSubmitted,
      sapOrderNumber,
      currencyCode
    } = orderDetails;

    const { departmentName } = mapDepartmentName(
      [{ departmentNumber: departmentNumber }],
      [...billableDepartments, ...userAllDepartments],
      billableDepartments.length > 0 ? [accountNumber, billableDepartments[0].customerNumber] : [accountNumber]
    )[0];

    const totalQuantity = this.getTotal();
    const totalAmount = PAYMENT_METHOD_FREE === paymentMethod ? 0 : this.getSubmittableReturnListWholesaleTotal();
    const totalItems = this.getTotalItems();

    const displayList = this.getHardFilteredList(true);

    return (
      <div className='u-full-width flex-container align-center bg-primary-grey'>
        {section !== SECTION_SUBMITTED && (
          <OrderReturnsNavigation
            backTo={this.getHeaderPath()}
            backToText={
              previousLocationWasReturnStatus || originatesFromReturnStatus
                ? 'global.backToReturns'
                : 'global.backToOrders'
            }
            onBackClickHandler={this.onBackClickHandler}
          />
        )}

        <div className='orderReturns flex-container column u-full-width align-center'>
          {section === SECTION_SUBMITTED ? (
            <OrderReturnsSubmittedBar />
          ) : (
            <div
              className={classnames(
                'orderReturns__header',
                'bg-white pt5-sm flex-container center u-full-width border-bottom'
              )}
            >
              <StepperStyled activeStep={currentStep} className='pb5-sm' style={{ minWidth: '500px' }}>
                {steps.map((step, index) => (
                  <StepStyled
                    key={step.section}
                    onClick={() => {
                      if (index >= currentStep) {
                        return;
                      }
                      this.setState({ currentStep: index });
                    }}
                    style={{ cursor: index < currentStep ? 'pointer' : 'default' }}
                  >
                    <StepLabelStyled>{step.label}</StepLabelStyled>
                  </StepStyled>
                ))}
              </StepperStyled>
            </div>
          )}
          <div className='orderReturns__section flex-container column mb5-sm u-full-width'>
            <div className='orderReturns__section_pageHeader d-sm-b d-md-h u-full-width'>
              {section === SECTION_SELECT && (
                <>
                  <OrderReturnsPageSelectHeader
                    returnQuantity={totalQuantity}
                    returnAmount={totalAmount}
                    currencyCode={currencyCode}
                  />
                  <OrderReturnsPageSearch
                    updateSearchTerm={this.updateSearchTerm}
                    searchTerm={searchTerm}
                    selectAllProducts={this.selectAllProducts}
                    showSelectAll={displayList && displayList.length > 0}
                  />
                </>
              )}
            </div>
            <div className='orderReturns__section_page prl3-sm prl0-md mr4-md mt5-sm u-full-width'>
              <div className='orderReturns__section_page_headerContainer d-sm-h d-md-b'>
                {section === SECTION_SELECT && (
                  <>
                    <OrderReturnsPageSelectHeader
                      returnQuantity={totalQuantity}
                      returnAmount={totalAmount}
                      currencyCode={currencyCode}
                    />
                    <OrderReturnsPageSearch
                      updateSearchTerm={this.updateSearchTerm}
                      searchTerm={searchTerm}
                      selectAllProducts={this.selectAllProducts}
                      showSelectAll={displayList && displayList.length > 0}
                    />
                  </>
                )}
              </div>
              {section === SECTION_DETAIL && (
                <OrderReturnsPageDetails
                  referenceId={
                    returnsForm?.values?.returnReference && returnsForm.values.returnReference !== ''
                      ? returnsForm.values.returnReference
                      : sapOrderNumber || TODAY
                  }
                  onFormValueUpdate={this.onFormValueUpdate}
                />
              )}
              {section === SECTION_SELECT && (
                <OrderReturnsPageSelect
                  hasMore={hasMore}
                  loadInfiniteScrollReturns={this.loadInfiniteScrollReturns}
                  products={displayList}
                  returnableProducts={this.getReturnableProducts()}
                  getReturnData={this.getReturnableProduct}
                  returnsForm={returnsForm}
                  onFormValueUpdate={this.onQuantityFormValueUpdate}
                  currencyCode={currencyCode}
                  isSelectAllProducts={isSelectAllProducts}
                  setIsSelectAllProducts={this.setIsSelectAllProducts}
                  submittableReturnsList={submittableReturnsList}
                  setHasQuantityError={this.setHasQuantityError}
                />
              )}
              {section === SECTION_FINALIZE && (
                <>
                  <OrderReturnsPageFinalContact
                    setInitialContactForm={this.setInitialContactForm}
                    setCurrentAddress={this.setCurrentAddress}
                    onFormValueUpdate={this.onFormValueUpdate}
                    accountNumber={orderDetails.accountNumber}
                    shipToId={orderDetails.shipToId}
                    currentAddress={currentAddress}
                  />
                  <OrderReturnsPageFinalDetails
                    products={displayList}
                    getReturnData={this.getReturnableProduct}
                    returnsForm={returnsForm}
                    currencyCode={currencyCode}
                    submittableReturnsList={submittableReturnsList}
                  />
                </>
              )}
              {section === SECTION_SUBMITTED && (
                <OrderReturnsPageSubmitted
                  returnData={returnData}
                  products={displayList}
                  downloadHandler={this.download}
                  printHandler={this.print}
                  getReturnData={this.getReturnableProduct}
                  returnsForm={returnsForm}
                  currencyCode={currencyCode}
                  submittedReturnDetails={submittedReturnDetails}
                  submittableReturnsList={submittableReturnsList}
                />
              )}
            </div>
            <StickyBox
              className='stickyBox u-full-width'
              offsetTop={25}
              offsetBottom={0}
              bottom
              style={{ height: '100%' }}
            >
              <div className='orderReturns__section_status mt5-sm bg-white u-full-width'>
                <OrderReturnsStatusSummary
                  accountName={toTitleCase(accountName, false)}
                  department={departmentName ? toTitleCase(departmentName) : ''}
                  paymentMethod={paymentMethod}
                  orderType={orderType}
                  poNumber={customerPO}
                  orderSubmittedBy={submittedBy}
                  orderSubmittedDate={orderSubmitted}
                  orderNumber={sapOrderNumber}
                />
                <OrderReturnsStatusDetails
                  currentStep={currentStep}
                  returnReason={
                    returnsForm && returnsForm.values && returnsForm.values.returnReason
                      ? RETURN_REASONS.filter(reason => reason.value === returnsForm.values.returnReason)[0].label
                      : ''
                  }
                  returnComments={returnsForm && returnsForm.values ? returnsForm.values.returnComments : ''}
                  returnReferenceId={returnsForm && returnsForm.values ? returnsForm.values.returnReference : ''}
                  totalQuantity={totalQuantity}
                  totalItems={totalItems}
                />
                <OrderReturnsStatusFinal
                  currentStep={currentStep}
                  section={section}
                  returnAmount={totalAmount}
                  currencyCode={currencyCode}
                  totalQuantity={totalQuantity}
                  sectionProgress={this.sectionProgress}
                  submitting={isSubmitting}
                  detailUpdate={detailUpdate}
                  valid={valid}
                  hasQuantityError={hasQuantityError}
                  displayList={displayList}
                  isSaving={isSaving}
                  paymentMethod={paymentMethod}
                  salesOrg={salesOrg}
                  currentAddress={currentAddress}
                  submittedReturns={submittedReturns}
                />
              </div>
            </StickyBox>
          </div>
        </div>
      </div>
    );
  }
}

OrderReturns.propTypes = {
  ...formPropTypes,
  account: userAccountTypes.isRequired,
  billableDepartments: departmentsTypes,
  createNewReturn: PropTypes.func.isRequired,
  createReturnResponse: PropTypes.shape({
    returnableProducts: PropTypes.array
  }),
  isFetching: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  requestUnsubmittedReturnDetails: PropTypes.func.isRequired,
  returnData: PropTypes.object,
  setIsReturnStep: PropTypes.func.isRequired,
  setOriginatesFromReturnStatus: PropTypes.func.isRequired,
  setReturnData: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  submitReturn: PropTypes.func.isRequired,
  updateReturnDetails: PropTypes.func.isRequired,
  user: userTypes.isRequired,
  userAllDepartments: PropTypes.arrayOf(userDepartmentTypes).isRequired,
  previousLocationWasReturnStatus: PropTypes.bool.isRequired,
  originatesFromReturnStatus: PropTypes.bool.isRequired,
  setSelectedReturnId: PropTypes.func.isRequired,
  selectedReturnId: PropTypes.string.isRequired,
  disableModal: PropTypes.func.isRequired,
  clearPreviousReturnStepData: PropTypes.func.isRequired,
  detailUpdate: PropTypes.bool.isRequired,
  submittedReturnDetails: PropTypes.array,
  requestReturnsById: PropTypes.func.isRequired
};

OrderReturns.defaultProps = {
  billableDepartments: null,
  createReturnResponse: {
    returnableProducts: null
  },
  returnData: null,
  submittedReturnDetails: []
};

const mapStateToProps = state => ({
  account: getUserAccount(state),
  billableDepartments: billableDepartments(state),
  createReturnResponse: getReturnCreateResponse(state),
  detailUpdate: detailUpdate(state),
  isFetching: isFetching(state),
  isSubmitting: isSubmitting(state),
  orderDetails: orderDetailsData(state),
  orderReturnDetails: getOrderReturnDetails(state),
  originatesFromReturnStatus: originatesFromReturnStatus(state),
  previousLocationWasReturnStatus: previousLocationWasReturnStatus(state),
  returnData: getReturnData(state),
  returnsForm: state.form.orderReturnsForm,
  searchTerm: searchTerm(state),
  user: getUserData(state),
  userAllDepartments: selectDepartments(state),
  selectedReturnId: selectedReturnId(state),
  submittedReturnDetails: getSubmittedReturnDetails(state),
  submittedReturns: submittedReturns(state),
  salesOrg: getSalesOrg(state)
});

const connector = connect(mapStateToProps, {
  createNewReturn,
  requestUnsubmittedReturnDetails,
  requestSubmittedReturns,
  setIsReturnStep,
  setOriginatesFromReturnStatus,
  setReturnData,
  showToast,
  submitReturn,
  updateReturnDetails,
  openModal,
  closeModal,
  setSelectedReturnId,
  disableModal,
  clearPreviousReturnStepData,
  requestOrderDetails,
  requestReturnsById
});

export default reduxForm({
  form: 'orderReturnsForm'
})(connector(OrderReturns));
